import { useEffect, useRef, useState } from 'react';

// The guide is read from the one file the repository keeps it in, so the page
// and `docs/approving-a-video.md` can never tell the operator different things.
import guide from '../../docs/approving-a-video.md?raw';
import { Icon } from './Icon.tsx';
import { Markdown } from './ui.tsx';
import * as wording from './wording.ts';

/**
 * The guide as the dialog draws it: without its `# How approval works` line,
 * which the dialog's own title already says, and with each paragraph on one
 * line. The file is wrapped at 80 columns, and `markdown.ts` keeps soft breaks
 * and reads `**bold**` only within a line.
 */
const GUIDE_BODY = guide
  .replace(/^# .*\n/, '')
  .split(/\n{2,}/)
  .map((block) => (block.startsWith('#') ? block : block.replace(/\s*\n\s*/g, ' ')))
  .join('\n\n');

/**
 * "How approval works" (US-113): a link on the narration and scenes panels
 * that opens the plain-language guide to both approvals in a dialog, so the
 * operator can learn the flow without leaving the decision in front of them.
 * A button, not an `<a>`: it opens something on the page rather than going
 * anywhere.
 */
export function HowApprovalWorks() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    // `showModal()` throws if it is already open (StrictMode runs this twice).
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <>
      <button
        type="button"
        className="guide-link"
        aria-haspopup="dialog"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Icon name="info" />
        {wording.HOW_APPROVAL_WORKS}
      </button>
      <dialog
        className="dialog dialog--guide"
        ref={ref}
        aria-labelledby="approval-guide-title"
        // Esc closes the dialog natively; keep the state in step with it.
        onClose={() => {
          setOpen(false);
        }}
      >
        <h2 id="approval-guide-title" className="dialog__title">
          {wording.HOW_APPROVAL_WORKS}
        </h2>
        <div className="dialog__body">{open && <Markdown text={GUIDE_BODY} />}</div>
        <div className="dialog__actions">
          <button
            type="button"
            className="button"
            autoFocus
            onClick={() => {
              setOpen(false);
            }}
          >
            {wording.CLOSE}
          </button>
        </div>
      </dialog>
    </>
  );
}

const SCENES_INTRO_KEY = 'mc.sketch.intro-hidden';
const IDEA_REVIEW_INTRO_KEY = 'mc.idea-review.intro-hidden';

/** Storage can throw (a private window, a full quota); then the introduction is simply shown again. */
function readIntroHidden(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === 'yes';
  } catch {
    return false;
  }
}

function writeIntroHidden(key: string): void {
  try {
    window.localStorage.setItem(key, 'yes');
  } catch {
    // Not remembered: it hides now and comes back on the next visit.
  }
}

/**
 * A short note the first time this browser sees a screen, until **Got it** is
 * pressed; `storageKey` remembers that per screen.
 */
function Introduction({ storageKey, text }: { readonly storageKey: string; readonly text: string }) {
  const [hidden, setHidden] = useState(() => readIntroHidden(storageKey));
  if (hidden) return null;
  return (
    <div className="scenes-intro" role="note">
      <p>{text}</p>
      <button
        type="button"
        className="button button--small"
        onClick={() => {
          writeIntroHidden(storageKey);
          setHidden(true);
        }}
      >
        {wording.GOT_IT}
      </button>
    </div>
  );
}

/** Three sentences above the scenes the first time this browser sees them (US-113). */
export function ScenesIntro() {
  return <Introduction storageKey={SCENES_INTRO_KEY} text={wording.SCENES_INTRO} />;
}

/** "What am I looking at", above the first idea of the one-by-one review (US-124). */
export function IdeaReviewIntro() {
  return <Introduction storageKey={IDEA_REVIEW_INTRO_KEY} text={wording.IDEA_REVIEW_INTRO} />;
}
